<template>
    <div class="image-viewer-overlay pt-3 pb-3">
        <!-- Close button -->
        <button class="btn-close" @click="$emit('close')" aria-label="Close">&times;</button>
        
        <!-- Main image display -->
        <div class="main-image-container pt-5">
            <img 
                :src="$url + '/' + currentImage" 
                :alt="item.title" 
                class="main-image"
                @click="nextImage"
            />
            
            <!-- Navigation arrows -->
            <button v-if="hasMultipleImages" class="nav-btn prev-btn" @click="previousImage">
                &#8249;
            </button>
            <button v-if="hasMultipleImages" class="nav-btn next-btn" @click="nextImage">
                &#8250;
            </button>
        </div>
        
        <!-- Image counter -->
        <div v-if="hasMultipleImages" class="image-counter">
            {{ currentIndex + 1 }} / {{ totalImages }}
        </div>
        
        <!-- Thumbnails -->
        <div v-if="hasMultipleImages" class="thumbnails">
            <div 
                v-for="(image, index) in allImages" 
                :key="index"
                class="thumbnail"
                :class="{ active: index === currentIndex }"
                @click="goToImage(index)"
            >
                <img :src="$url + '/' + image" :alt="`${item.title} - Image ${index + 1}`" />
            </div>
        </div>
        
        <!-- Item info -->
        <div class="item-info d-flex">
            <h3>{{ item.title }}</h3>
            <span v-if="item.year">, {{ item.year }}</span>
            <span v-if="item.media_es || item.media_en">
                , {{ $store.state.lang === 'es' ? item.media_es : item.media_en }}
            </span>
            <span v-if="item.dimensions">, {{ item.dimensions }}</span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ImageViewer',
    props: {
        item: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            currentIndex: 0
        }
    },
    computed: {
        allImages() {
            const images = []
            
            if (this.item.extra_images && Array.isArray(this.item.extra_images)) {
                images.push(...this.item.extra_images)
            }
            return images
        },
        currentImage() {
            return this.allImages[this.currentIndex] || this.item.featured
        },
        hasMultipleImages() {
            return this.allImages.length > 1
        },
        totalImages() {
            return this.allImages.length
        }
    },
    methods: {
        nextImage() {
            if (this.hasMultipleImages) {
                this.currentIndex = (this.currentIndex + 1) % this.totalImages
            }
        },
        previousImage() {
            if (this.hasMultipleImages) {
                this.currentIndex = this.currentIndex === 0 ? this.totalImages - 1 : this.currentIndex - 1
            }
        },
        goToImage(index) {
            this.currentIndex = index
        }
    }
}
</script>

<style scoped>
.image-viewer-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: #000;
    z-index: 99999;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.btn-close {
    position: absolute;
    top: 20px;
    right: 20px;
    background: none;
    border: none;
    color: white;
    font-size: 2rem;
    cursor: pointer;
    padding: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}

.btn-close:hover {
    color: #ccc;
}

.main-image-container {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 70vh;
    margin-bottom: 20px;
}

.main-image {
    max-width: 90%;
    max-height: 100%;
    object-fit: contain;
    cursor: pointer;
}

.nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.7);
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
    padding: 15px 10px;
    border-radius: 5px;
    transition: background-color 0.3s;
}

.nav-btn:hover {
    background-color: rgba(255, 255, 255, 0.2);
}

.prev-btn {
    left: 20px;
}

.next-btn {
    right: 20px;
}

.image-counter {
    position: absolute;
    top: 20px;
    left: 20px;
    color: white;
    background-color: rgba(0, 0, 0, 0.7);
    padding: 5px 10px;
    border-radius: 15px;
    font-size: 14px;
}

.thumbnails {
    display: flex;
    justify-content: center;
    gap: 10px;
    padding: 20px;
    background-color: rgba(0, 0, 0, 0.8);
    border-radius: 8px;
    
}

.thumbnail {
    width: 80px;
    height: 80px;
    cursor: pointer;
    border: 3px solid transparent;
    border-radius: 5px;
    overflow: hidden;
    transition: border-color 0.3s;
}

.thumbnail.active {
    border-color: #fff;
}

.thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.thumbnail:hover {
    border-color: rgba(255, 255, 255, 0.5);
}

.item-info {
    padding: 20px;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    text-align: center;
    border-radius: 8px;
}

.item-info h3 {
    margin: 0 0 10px 0;
    font-size: 18px;
}

.item-info p {
    margin: 5px 0;
    font-size: 14px;
    opacity: 0.8;
}

@media (max-width: 768px) {
    .main-image {
        max-width: 95%;
    }
    
    .thumbnails {
        padding: 15px;
        gap: 8px;
    }
    
    .thumbnail {
        width: 60px;
        height: 60px;
    }
    
    .nav-btn {
        padding: 10px 8px;
        font-size: 20px;
    }
}
</style> 